import { ComponentProps } from 'react'

import { ResizableLayout } from '@/components/layouts'
import { InquiryList } from '@/components/shared/InquiryList'
import { Box, Stack } from '@/components/ui'
import { useGqlQuerySuspense } from '@/lib/api'
import { usePermission } from '@/lib/auth'
import { useBreakpointValue } from '@/lib/chakra-ui'
import { graphql } from '@/lib/gql'

import { InquiryDialog } from './InquiryDialog'
import { RelatedTrialList } from './RelatedTrialList'
import { SPTrialDetail, TrialDetail } from './TrialDetail'
import { NewInquiryContextProvider, useNewInquiry } from './useNewInquiry'

const getTrialDetailContentQuery = graphql(`
  query GetTrialDetail($trialNumber: String!) {
    trial(trialNumber: $trialNumber) {
      trialUid
      networkUid
      ...TrialDetail
      ...InquiryDialogTrial
      relatedTrials {
        ...RelatedTrialsTrial
      }
    }
  }
`)

export const TrialDetailPage = ({
  trialNumber,
  inquiryUid,
}: {
  trialNumber: string
  inquiryUid?: string
}) => {
  const { data } = useGqlQuerySuspense({
    document: getTrialDetailContentQuery,
    variable: { trialNumber },
  })

  const renderSPMode = useBreakpointValue({ base: true, desktop: false })

  const relatedTrials = data.trial.relatedTrials

  const inquiryDialogWidth = 720
  const inquiryDialogHeight = 720
  const inquiryDialogPadding = 8

  const newInquiryState = useNewInquiry()

  const { hasPermission } = usePermission()

  const inquiryListProps: ComponentProps<typeof InquiryList> = {
    listTitle: `試験ID ${trialNumber}に関する問い合わせ`,
    showActionButton: hasPermission('InquiryPost', data.trial.networkUid),
    trialUid: data.trial.trialUid,
  }

  if (renderSPMode) {
    return (
      <NewInquiryContextProvider value={newInquiryState}>
        <SPTrialDetail
          trialDetail={data.trial}
          relatedTrialArea={
            relatedTrials.length > 0 && (
              <RelatedTrialList relatedTrials={relatedTrials} />
            )
          }
          inquiryArea={<InquiryList {...inquiryListProps} />}
        />
        <InquiryDialog
          isOpen={!!inquiryUid || newInquiryState.showInquiryDialog}
          inquiryUid={inquiryUid}
          trial={data.trial}
          defaultLayout={{
            position: {
              x: window.innerWidth - inquiryDialogWidth,
              y: window.innerHeight - inquiryDialogHeight,
            },
            size: { width: inquiryDialogWidth, height: inquiryDialogHeight },
          }}
        />
      </NewInquiryContextProvider>
    )
  }

  return (
    <NewInquiryContextProvider value={newInquiryState}>
      <ResizableLayout
        mainContent={<TrialDetail trialDetail={data.trial} />}
        resizableContent={
          <Stack h="full" gap={0} overflow="auto">
            {relatedTrials.length > 0 && (
              <Box
                maxH="280px"
                borderBottom="1px solid"
                borderColor="border.gray"
              >
                <RelatedTrialList relatedTrials={relatedTrials} />
              </Box>
            )}
            <Box flex="1" overflow="auto" minH="320px">
              <InquiryList {...inquiryListProps} />
            </Box>
          </Stack>
        }
      />
      <InquiryDialog
        isOpen={!!inquiryUid || newInquiryState.showInquiryDialog}
        inquiryUid={inquiryUid}
        trial={data.trial}
        defaultLayout={{
          position: {
            x: window.innerWidth - (inquiryDialogWidth + inquiryDialogPadding),
            y:
              window.innerHeight -
              (inquiryDialogHeight + inquiryDialogPadding + 80),
          },
          size: { width: inquiryDialogWidth, height: inquiryDialogHeight },
        }}
      />
    </NewInquiryContextProvider>
  )
}
